import styled from 'styled-components';

import Button from 'components/shared/Button';
import { simpleDateAtTime } from 'lib/date';
import { WorkflowAction } from 'models/Workflow';
import { getEligibilityPdfUrl } from 'services/api/workflows';
import { useToastActions } from 'stores/toastStore';
import { colors } from 'styles/theme/colors';
import { BodySmall } from 'styles/typography';

import { LinkButton } from './BenefitsTable';

interface Props {
  workflowId: string;
  // The coverage's verification. Its completion is what "last ran" means — the payer search running
  // again isn't a verification run.
  benefits: WorkflowAction | undefined;
  facetId: string | null;
  // False when the run produced no downloadable document (an error, or a payer that isn't enrolled).
  pdfAvailable: boolean;
  // Omitted for a reader who may not re-run this verification. The footer still renders — when
  // the check last ran and its PDF are readings of the result — it just carries no re-run.
  onRerun?: () => void;
  isRerunning: boolean;
  // The plain-language benefits view's footer: a Download PDF link and a small Re-run button.
  compact?: boolean;
}

// The pinned footer for a coverage: when it last ran and who ran it, the PDF of that run, and the
// re-run. Sits outside the scrolling body (see DetailSlideout) because a benefits result is long
// enough that a footer inside it would only be found by scrolling to the end.
export default function EligibilityFooter({
  workflowId,
  benefits,
  facetId,
  pdfAvailable,
  onRerun,
  isRerunning,
  compact = false,
}: Props) {
  const { addToast } = useToastActions();

  const downloadPdf = () => {
    // A fresh short-lived signed URL per click, scoped to this coverage — no client-side generation.
    getEligibilityPdfUrl(workflowId, facetId)
      .then(({ url }) => window.open(url, '_blank'))
      .catch(() => addToast({ text: 'Could not download the PDF. Please try again.' }));
  };

  return (
    <>
      <LastRan data-testid='eligibility-last-ran'>
        {benefits?.completedAt
          ? `Last ran on ${simpleDateAtTime(benefits.completedAt)}${
              benefits.completedByName ? ` by ${benefits.completedByName}` : ''
            }`
          : ''}
      </LastRan>
      <Actions>
        {pdfAvailable &&
          (compact ? (
            <LinkButton type='button' onClick={downloadPdf} data-testid='download-eligibility-pdf'>
              Download PDF
            </LinkButton>
          ) : (
            <Button variant='outline-primary' onClick={downloadPdf} data-testid='download-eligibility-pdf'>
              Download PDF
            </Button>
          ))}
        {/* Only once there's something to re-run. A coverage waiting on its first check used to get
            this button disabled, which put a dead control directly under the live "Run Eligibility
            Check" on the payer-selection panel — two buttons, one of which could never be pressed.
            What that coverage is waiting on is a payer or a member ID, and the panel above says so. */}
        {benefits?.completedAt && benefits.rerunnable && onRerun && (
          <Button
            onClick={onRerun}
            disabled={isRerunning}
            loading={isRerunning}
            variant={compact ? 'outline-primary' : 'primary'}
            size={compact ? 'small' : 'default'}
            data-testid='rerun-eligibility'>
            {compact ? 'Re-run' : 'Re-Run Benefit Verification'}
          </Button>
        )}
      </Actions>
    </>
  );
}

const LastRan = styled(BodySmall)`
  color: ${colors.black50};
`;

const Actions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;
